/**
 * Criptografia do login de demonstração (SPEC-v2 2.2, ADR-009): só WebCrypto, nada de biblioteca.
 *
 * - Senha: PBKDF2-SHA256 com sal aleatório de 16 bytes e 600.000 iterações (OWASP 2023).
 * - Tokens (sessão, convite, redefinição, desafio do 2FA): 32 bytes aleatórios em base64url; o
 *   armazenamento guarda só o SHA-256 do token.
 * - 2FA: TOTP do RFC 6238 (HMAC-SHA1, 6 dígitos, passo de 30 s, tolerância de ±1 passo).
 *
 * Comparações de segredo em tempo constante (`equalBytes`).
 */

export const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const HASH_BYTES = 32;

function subtle(): SubtleCrypto {
  const c = globalThis.crypto;
  if (!c?.subtle) throw new Error('WebCrypto indisponível neste navegador.');
  return c.subtle;
}

export function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(n));
  globalThis.crypto.getRandomValues(out);
  return out;
}

export function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Token opaco de 256 bits (sessão, convite, redefinição). */
export function newToken(): string {
  return toBase64Url(randomBytes(32));
}

/** Id curto e aleatório para registros (não é segredo). */
export function newId(prefix: string): string {
  return `${prefix}_${toBase64Url(randomBytes(9))}`;
}

export async function sha256(text: string): Promise<string> {
  const digest = await subtle().digest('SHA-256', new TextEncoder().encode(text));
  return toBase64Url(new Uint8Array(digest));
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}

export function equalStrings(a: string, b: string): boolean {
  const enc = new TextEncoder();
  return equalBytes(enc.encode(a), enc.encode(b));
}

/** O que o IndexedDB guarda por pessoa: nunca a senha. */
export interface PasswordRecord {
  algo: 'PBKDF2-SHA256';
  salt: string;
  iterations: number;
  hash: string;
}

async function derive(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<Uint8Array> {
  const key = await subtle().importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await subtle().deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    key,
    HASH_BYTES * 8,
  );
  return new Uint8Array(bits);
}

export async function hashPassword(
  password: string,
  iterations = PBKDF2_ITERATIONS,
): Promise<PasswordRecord> {
  const salt = randomBytes(SALT_BYTES);
  const hash = await derive(password, salt, iterations);
  return { algo: 'PBKDF2-SHA256', salt: toBase64Url(salt), iterations, hash: toBase64Url(hash) };
}

export async function verifyPassword(password: string, rec: PasswordRecord): Promise<boolean> {
  const hash = await derive(password, fromBase64Url(rec.salt), rec.iterations);
  return equalBytes(hash, fromBase64Url(rec.hash));
}

// ---------------------------------------------------------------------------
// TOTP (RFC 6238 / RFC 4226)
// ---------------------------------------------------------------------------

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function toBase32(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += B32[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += B32[(value << (5 - bits)) & 31];
  return out;
}

export function fromBase32(s: string): Uint8Array<ArrayBuffer> {
  const clean = s.toUpperCase().replace(/[^A-Z2-7]/g, '');
  let bits = 0;
  let value = 0;
  const out: number[] = [];
  for (const ch of clean) {
    value = (value << 5) | B32.indexOf(ch);
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 255);
      bits -= 8;
    }
  }
  return new Uint8Array(out);
}

export function newTotpSecret(): string {
  return toBase32(randomBytes(20));
}

export async function totpAt(secretB32: string, at: number, step = 30): Promise<string> {
  const counter = Math.floor(at / 1000 / step);
  const msg = new Uint8Array(new ArrayBuffer(8));
  const view = new DataView(msg.buffer);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter >>> 0);
  const key = await subtle().importKey('raw', fromBase32(secretB32), { name: 'HMAC', hash: 'SHA-1' }, false, [
    'sign',
  ]);
  const mac = new Uint8Array(await subtle().sign('HMAC', key, msg));
  const offset = (mac[mac.length - 1] ?? 0) & 15;
  const bin =
    (((mac[offset] ?? 0) & 0x7f) << 24) |
    ((mac[offset + 1] ?? 0) << 16) |
    ((mac[offset + 2] ?? 0) << 8) |
    (mac[offset + 3] ?? 0);
  return String(bin % 1_000_000).padStart(6, '0');
}

/** Confere o código com tolerância de ±1 passo (relógio do celular um pouco adiantado ou atrasado). */
export async function verifyTotp(secretB32: string, code: string, now: number): Promise<boolean> {
  if (!/^\d{6}$/.test(code)) return false;
  for (const delta of [0, -30_000, 30_000]) {
    if (equalStrings(await totpAt(secretB32, now + delta), code)) return true;
  }
  return false;
}

/** 10 códigos de recuperação no formato `xxxx-xxxx` (minúsculas e dígitos, sem ambíguos). */
export function newRecoveryCodes(n = 10): string[] {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  return Array.from({ length: n }, () => {
    const bytes = randomBytes(8);
    const chars = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
    return `${chars.slice(0, 4)}-${chars.slice(4)}`;
  });
}
